import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  PageSection,
  Skeleton,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../../app/api";
import type { DashboardOrder, Location, LocationsPayload, Product, ProductsPayload, PurchaseOrdersPayload, User } from "../../types";
import {
  actionLabel,
  dateDaysFromNow,
  formatNumber,
  formatQuantity,
  locationLabel,
  plainNextAction,
  productLabel,
  requestDateText,
  requestDocumentNumber,
  requestLocationText
} from "../../display";

type RequestLine = {
  key: string;
  productId: string;
  quantity: string;
};

type CreateBranchRequestResult = {
  ok: boolean;
  po_id?: number;
  po_number?: string;
  request_id?: number;
  request_number?: string;
};

function isInHouseLocation(location: Location | undefined): boolean {
  return String(location?.code || "").toUpperCase().startsWith("IH");
}

function requestLocationOptions(user: User, locations: Location[]): Location[] {
  const assignedLocation = locations.find(location => Number(location.id) === Number(user.location_id || 0));
  if (assignedLocation && String(assignedLocation.location_type || "").toLowerCase() === "branch") {
    return [assignedLocation];
  }
  const operationalLocations = locations.filter(location => String(location.location_type || "").toLowerCase() === "branch");
  return operationalLocations.length ? operationalLocations : locations;
}

function selectableRequestProducts(products: Product[]): Product[] {
  return products.filter(product => {
    const active = Number((product as { active?: number }).active ?? 1) !== 0;
    const available = Number(product.bor_available_here ?? product.available_for_bor ?? 1) !== 0;
    return active && available;
  });
}

export function BranchRequestsWorkspace({
  user,
  csrfToken,
  onOpenOrder
}: {
  user: User;
  csrfToken: string;
  onOpenOrder: (id: string | number) => Promise<void>;
}) {
  const [orders, setOrders] = useState<DashboardOrder[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [requestLocationId, setRequestLocationId] = useState("");
  const [requestDate, setRequestDate] = useState(dateDaysFromNow(0));
  const [neededBy, setNeededBy] = useState(dateDaysFromNow(1));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<RequestLine[]>([{ key: crypto.randomUUID(), productId: "", quantity: "" }]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("attention");
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<PurchaseOrdersPayload>("/api/purchase-orders?summary=1");
      setOrders(payload.purchase_orders || []);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Branch Requests could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    try {
      const payload = await apiGet<LocationsPayload>("/api/locations");
      const options = requestLocationOptions(user, payload.locations || []);
      setLocations(options);
      setRequestLocationId(current => {
        if (current && options.some(location => String(location.id) === current)) return current;
        if (user.location_id && options.some(location => Number(location.id) === Number(user.location_id))) return String(user.location_id);
        return String(options[0]?.id || "");
      });
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Request locations could not be loaded." });
    }
  }, [user]);

  const loadProducts = useCallback(async (locationId: string) => {
    if (!locationId) {
      setProducts([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const payload = await apiGet<ProductsPayload>(`/api/products?location_id=${encodeURIComponent(locationId)}`);
      const options = selectableRequestProducts(payload.products || []);
      setProducts(options);
      setLines(current => current.map(line => {
        if (line.productId && options.some(product => String(product.id) === line.productId)) return line;
        return { ...line, productId: String(options[0]?.id || "") };
      }));
    } catch (err) {
      setProducts([]);
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Request items could not be loaded." });
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    const reload = () => void loadRequests();
    window.addEventListener("toj:orders-changed", reload);
    return () => window.removeEventListener("toj:orders-changed", reload);
  }, [loadRequests]);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    void loadProducts(requestLocationId);
  }, [loadProducts, requestLocationId]);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      const matchesStatus = (
        statusFilter === "all"
        || (statusFilter === "attention" && order.next_action_is_mine !== false)
        || (statusFilter === "open" && !status.startsWith("closed") && status !== "resolved")
        || (statusFilter === "contested" && (status.includes("contest") || key.includes("contest")))
        || (statusFilter === "unserved" && Number(order.unserved_total || 0) > 0)
      );
      if (!matchesStatus) return false;
      if (!term) return true;
      return [
        requestDocumentNumber(order),
        requestLocationText(order),
        order.status,
        order.next_action,
        order.requested_delivery,
        order.created_at,
      ].some(value => String(value || "").toLowerCase().includes(term));
    });
  }, [orders, search, statusFilter]);

  const totals = useMemo(() => ({
    attention: orders.filter(order => order.next_action_is_mine !== false).length,
    open: orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      return !status.startsWith("closed") && status !== "resolved";
    }).length,
    contested: orders.filter(order => {
      const status = String(order.status || "").toLowerCase();
      const key = String(order.next_action_key || "").toLowerCase();
      return status.includes("contest") || key.includes("contest");
    }).length,
    unserved: orders.reduce((sum, order) => sum + Number(order.unserved_total || 0), 0)
  }), [orders]);

  const selectedLocation = useMemo(() => (
    locations.find(location => String(location.id) === requestLocationId)
  ), [locations, requestLocationId]);

  const selectedRequestIsInHouse = isInHouseLocation(selectedLocation);

  const readyLines = useMemo(() => lines.filter(line => {
    const quantity = Number(line.quantity || 0);
    return line.productId && Number.isFinite(quantity) && quantity > 0;
  }), [lines]);

  const updateLine = useCallback((key: string, patch: Partial<RequestLine>) => {
    setLines(current => current.map(line => line.key === key ? { ...line, ...patch } : line));
  }, []);

  const addLines = useCallback((count: number) => {
    setLines(current => {
      const next = [...current];
      while (next.length < 40 && count > 0) {
        next.push({ key: crypto.randomUUID(), productId: String(products[0]?.id || ""), quantity: "" });
        count -= 1;
      }
      return next;
    });
  }, [products]);

  const removeLine = useCallback((key: string) => {
    setLines(current => current.length <= 1 ? [{ ...current[0], productId: "", quantity: "" }] : current.filter(line => line.key !== key));
  }, []);

  const createRequest = useCallback(async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setMessage(null);
    if (!requestLocationId) {
      setMessage({ variant: "danger", title: "Choose the requesting location before sending the request." });
      return;
    }
    if (!readyLines.length) {
      setMessage({ variant: "danger", title: "Add at least one item with a quantity." });
      return;
    }
    setSaving(true);
    try {
      const requestLines = readyLines.map(line => ({
        product_id: Number(line.productId),
        quantity: Math.round(Number(line.quantity || 0)),
        allocations: [{ location_id: Number(requestLocationId), quantity: Math.round(Number(line.quantity || 0)) }]
      }));
      const endpoint = selectedRequestIsInHouse ? "/api/in-house-requests" : "/api/purchase-orders";
      const payload: Record<string, unknown> = {
        request_location_id: requestLocationId,
        request_date: requestDate,
        requested_delivery: neededBy,
        lines: selectedRequestIsInHouse
          ? requestLines.map(line => ({ product_id: line.product_id, quantity: line.quantity }))
          : requestLines
      };
      if (selectedRequestIsInHouse) payload.notes = notes;
      const result = await apiPost<CreateBranchRequestResult>(endpoint, payload, csrfToken);
      setMessage({ variant: "success", title: `Created ${result.po_number || result.request_number || "request"}.` });
      setNotes("");
      setLines([{ key: crypto.randomUUID(), productId: String(products[0]?.id || ""), quantity: "" }]);
      setCreateOpen(false);
      await loadRequests();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "The request could not be created." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, loadRequests, neededBy, notes, products, readyLines, requestDate, requestLocationId, selectedRequestIsInHouse]);

  const openOrder = useCallback(async (id: string | number) => {
    await onOpenOrder(id);
  }, [onOpenOrder]);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-branch-requests-view" className="pf-toj-branch-requests">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>Branch Requests</h1>
            <p>Create branch or in-house stock requests, then track review, filling, delivery, and branch confirmation.</p>
          </FlexItem>
          <FlexItem>
            <Button variant="primary" onClick={() => setCreateOpen(current => !current)}>
              {createOpen ? "Close request form" : "Create request"}
            </Button>
          </FlexItem>
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        {createOpen && (
          <Card className="pf-toj-work-card pf-toj-primary-work-card pf-toj-request-builder">
            <CardBody>
              <Form onSubmit={event => void createRequest(event)} className="pf-toj-form">
                <div className="pf-toj-field-row">
                  <FormGroup label="Requesting location" isRequired fieldId="pf-request-location">
                    <FormSelect
                      id="pf-request-location"
                      value={requestLocationId}
                      onChange={(_event, value) => setRequestLocationId(String(value))}
                      isDisabled={Boolean(user.location_id) || locations.length <= 1}
                    >
                      {locations.map(location => (
                        <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Request date" fieldId="pf-request-date">
                    <TextInput id="pf-request-date" type="date" value={requestDate} onChange={(_event, value) => setRequestDate(String(value))} />
                  </FormGroup>
                  <FormGroup label="Needed by" fieldId="pf-request-needed-by">
                    <TextInput id="pf-request-needed-by" type="date" value={neededBy} onChange={(_event, value) => setNeededBy(String(value))} />
                  </FormGroup>
                </div>
                {selectedRequestIsInHouse && (
                  <FormGroup label="Notes" fieldId="pf-request-notes">
                    <TextInput id="pf-request-notes" value={notes} onChange={(_event, value) => setNotes(String(value))} placeholder="Optional" />
                  </FormGroup>
                )}
                <div className="pf-toj-lines-head">
                  <div>
                    <strong>Request items</strong>
                    <span>{formatNumber(readyLines.length)} ready; {formatNumber(products.length)} available</span>
                  </div>
                  <div className="pf-toj-secondary-actions">
                    <Button variant="secondary" onClick={() => addLines(1)} isDisabled={lines.length >= 40 || !products.length}>Add item</Button>
                    <Button variant="secondary" onClick={() => addLines(5)} isDisabled={lines.length >= 40 || !products.length}>Add 5 items</Button>
                    <Button variant="secondary" onClick={() => addLines(40 - lines.length)} isDisabled={lines.length >= 40 || !products.length}>Show 40 lines</Button>
                  </div>
                </div>
                <div className="pf-toj-request-lines" aria-label="Branch request item lines">
                  <div className="pf-toj-request-line-head" aria-hidden="true">
                    <span />
                    <span>Item</span>
                    <span>Category</span>
                    <span>Unit</span>
                    <span>Quantity</span>
                    <span />
                  </div>
                  {lines.map((line, index) => {
                    const product = products.find(item => String(item.id) === line.productId);
                    return (
                      <div className="pf-toj-request-line" key={line.key}>
                        <span className="pf-toj-line-number">{index + 1}</span>
                        <FormSelect aria-label={`Request item line ${index + 1}`} value={line.productId} onChange={(_event, value) => updateLine(line.key, { productId: String(value) })}>
                          {products.map(item => (
                            <FormSelectOption key={item.id} value={String(item.id)} label={productLabel(item)} />
                          ))}
                        </FormSelect>
                        <span>{product?.request_category_label || product?.request_display_category || product?.category || "Uncategorized"}</span>
                        <span>{product?.uom || product?.bundle_box || "--"}</span>
                        <TextInput
                          aria-label={`Request quantity line ${index + 1}`}
                          type="number"
                          min="1"
                          step="1"
                          value={line.quantity}
                          onChange={(_event, value) => updateLine(line.key, { quantity: String(value) })}
                        />
                        <Button variant="plain" onClick={() => removeLine(line.key)} isDisabled={lines.length <= 1}>Remove</Button>
                      </div>
                    );
                  })}
                  {!products.length && !loadingProducts && (
                    <div className="pf-toj-empty">
                      <strong>No request items are available for this location.</strong>
                      <span>Check item visibility setup before creating a request.</span>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={saving}
                  isDisabled={saving || loadingProducts || !readyLines.length}
                  onClick={event => void createRequest(event)}
                >
                  Send request
                </Button>
              </Form>
            </CardBody>
          </Card>
        )}
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            <div className="pf-toj-request-toolbar">
              <TextInput
                aria-label="Search branch requests"
                placeholder="Search request number, branch, status, date, or next step"
                value={search}
                onChange={(_event, value) => setSearch(String(value))}
              />
              <FormSelect aria-label="Request status filter" value={statusFilter} onChange={(_event, value) => setStatusFilter(String(value))}>
                <FormSelectOption value="attention" label={`Needs attention (${formatNumber(totals.attention)})`} />
                <FormSelectOption value="open" label={`Open requests (${formatNumber(totals.open)})`} />
                <FormSelectOption value="contested" label={`Branch contested (${formatNumber(totals.contested)})`} />
                <FormSelectOption value="unserved" label={`Has unserved qty (${formatNumber(totals.unserved)})`} />
                <FormSelectOption value="all" label={`All visible (${formatNumber(orders.length)})`} />
              </FormSelect>
              <Button variant="secondary" onClick={() => void loadRequests()}>Refresh</Button>
            </div>
            {loading && <Skeleton height="180px" screenreaderText="Loading branch requests" />}
            {!loading && (
              <div className="pf-toj-table-wrap pf-toj-request-table-wrap">
                <Table aria-label="Branch request tracking" variant="compact">
                  <Thead>
                    <Tr>
                      <Th>Requester</Th>
                      <Th>Current Status</Th>
                      <Th>Needed By</Th>
                      <Th textCenter>Unserved</Th>
                      <Th>Available Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredOrders.map(order => (
                      <Tr key={String(order.id)}>
                        <Td dataLabel="Requester">
                          <strong>{requestDocumentNumber(order)}</strong>
                          <small>{requestLocationText(order)}</small>
                        </Td>
                        <Td dataLabel="Current Status">
                          <strong>{plainNextAction(order)}</strong>
                          <small>{order.status || "Open"}</small>
                        </Td>
                        <Td dataLabel="Needed By">{requestDateText(order.requested_delivery || order.created_at)}</Td>
                        <Td dataLabel="Unserved" textCenter>{formatQuantity(order.unserved_total)}</Td>
                        <Td dataLabel="Available Actions">
                          <Button variant="link" isInline onClick={() => void openOrder(order.id)}>
                            {actionLabel(order)}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                    {!filteredOrders.length && (
                      <Tr>
                        <Td colSpan={5}>No branch requests match this view.</Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>
            )}
            <p className="pf-toj-row-limit-note pf-toj-muted">
              Showing {formatNumber(filteredOrders.length)} of {formatNumber(orders.length)} visible request(s) for {user.role}.
            </p>
          </CardBody>
        </Card>
      </div>
    </PageSection>
  );
}
